import React, { useState } from 'react';
import { render } from 'react-dom';
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper";

(function init() {
  const selectors = {
    root: "[data-hero-slideshow]"
  }

  function loadComponent() {
    const rootElement = document.querySelector(selectors.root);
    const sectionId = rootElement?.getAttribute("data-section-id");
    const autoplayDelay = rootElement?.getAttribute("data-autoplay-delay");
    if (rootElement) render(<HeroSlideshow sectionId={sectionId} autoplayDelay={autoplayDelay} />, rootElement)
  }

  document.addEventListener("shopify:section:load", loadComponent);
  loadComponent();
})();

function HeroSlideshow({ sectionId, autoplayDelay }) {
  const slides = JSON.parse(document.querySelector("[data-hero-slides]").innerHTML);
  const [swiper, setSwiper] = useState();

  if (!slides) return null;

  return (
    <div className="relative">
      {slides.length > 1 &&
        <>
          <button className="absolute w-[3rem] h-[3rem] top-auto -bottom-5 md:top-[50%] -translate-y-2/4 left-0 md:left-4 z-10 flex justify-center items-center rounded-[50%] bg-taupe-200/20" onClick={() => swiper.slidePrev()}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-black">
              <path d="M9.021 0.824463L1.52099 7.79848L9.02099 14.7725" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
          <button className="absolute w-[3rem] h-[3rem] top-auto -bottom-5 md:top-[50%] -translate-y-2/4 right-0 md:right-4 z-10 flex justify-center items-center rounded-[50%] bg-taupe-200/20" onClick={() => swiper.slideNext()}>
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-black">
              <path d="M0.979004 15.2737L8.47901 8.29967L0.979006 1.32565" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </>
      }
      <div className="overflow-hidden relative">
        <Swiper
          onInit={(ev) => {
            setSwiper(ev)
          }}
          loop={slides.length > 1}
          autoplay={{
            delay: parseInt(autoplayDelay, 10),
            disableOnInteraction: true,
          }}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination, Autoplay]}
          slidesPerView={1}
          watchOverflow={true}
          className="swiper swiper--full-height"
        >
          {slides.map((slide, i) => {
            let css;
            if ('image_mobile' in slide) {
              css = `
              #HeroImage-${sectionId}-${i} {
                object-position: ${slide.image_mobile.focal_point}
              }

              @media (min-width: 768px) {
                #HeroImage-${sectionId}-${i} {
                  object-position: ${slide.image_desktop.focal_point}
                }
              }`
            } else {
              css = `
              #HeroImage-${sectionId}-${i} {
                object-position: ${slide.image_desktop.focal_point}
              }`
            }

            return (
              <SwiperSlide>
                <div className="relative aspect-[3/4] md:aspect-square lg:aspect-[5/3] xl:aspect-[2/1]">
                  <div className="absolute w-full h-full top-0 right-0">
                    {'image_mobile' in slide ?
                      <>
                        <style scoped>{css}</style>
                        <picture>
                          <source
                            media="(min-width: 48em)"
                            srcSet={slide.image_desktop.url}
                          />
                          <img
                            src={slide.image_mobile.url}
                            alt={slide.image_mobile.alt}
                            className="w-full h-full object-cover"
                            id={`HeroImage-${sectionId}-${i}`}
                          />
                        </picture>
                      </>
                    :
                      <img  
                        id={`HeroImage-${sectionId}-${i}`}
                        src={slide.image_desktop.url}
                        alt={slide.image_desktop.alt}
                        className="w-full h-full object-cover"
                      />
                    }
                  </div>
                  <div className="h-full relative max-w-[1264px] mx-auto flex flex-col justify-end text-black px-4 pt-[16rem] pb-[2rem] md:pb-[4rem]">
                    <div className="max-w-[800px]">
                      {slide.title && 
                        <h1 className={slide.title_size}>{slide.title}</h1>
                      }
                      <div className="hidden md:block">
                        {slide.subtitle &&
                          <h2 className="new-h6 pb-[1.5rem]">{slide.subtitle}</h2>
                        }
                        {(slide.primary_button_text || slide.secondary_button_text) &&
                          <div className="flex items-center justify-center md:justify-start flex-wrap">
                            {slide.primary_button_text && slide.primary_button_url &&
                              <a href={slide.primary_button_url} className="text-center w-full md:w-auto bg-black text-white font-semibold uppercase text-sm px-12 py-[1.125rem] grow-0">
                                {slide.primary_button_text}
                              </a>
                            }
                            {slide.secondary_button_text && slide.secondary_button_url &&
                              <div className="mt-[1rem] md:mt-0">
                                <span className="text-sm ml-[0.75rem] mr-[0.25rem]">or</span>
                                <a href={slide.secondary_button_url} className="w-full md:w-auto font-medium uppercase text-sm underline underline-offset-4">
                                  {slide.secondary_button_text}
                                </a>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
                <div className="md:hidden px-[1rem] pt-[2rem] pb-[2rem] bg-blush-200">
                  {slide.subtitle &&
                    <h2 className="new-h6 pb-[1.75rem] md:pt-[1.5rem] md:pb-[2.5rem]">{slide.subtitle}</h2>
                  }
                  {(slide.primary_button_text || slide.secondary_button_text) &&
                    <div className="flex items-center justify-center md:justify-start flex-wrap">
                      {slide.primary_button_text && slide.primary_button_url &&
                        <a href={slide.primary_button_url} className="text-center w-full md:w-auto bg-black text-white font-semibold uppercase text-sm px-12 py-[1.125rem] grow-0">
                          {slide.primary_button_text}
                        </a>
                      }
                      {slide.secondary_button_text && slide.secondary_button_url &&
                        <div className="mt-[1rem] md:mt-0">
                          <span className="text-sm ml-[0.75rem] mr-[0.25rem]">or</span>
                          <a href={slide.secondary_button_url} className="w-full md:w-auto font-medium uppercase text-sm underline underline-offset-4">
                            {slide.secondary_button_text}
                          </a>
                        </div>
                      }
                    </div>
                  }
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </div>
  )
}
